/**
 * Trunk rotation relative to the player's nearer table end line (−180…180°).
 * 0° = shoulder line parallel to that end line (square to the table).
 */

const MIN_SCORE = 0.12;
const MIN_SPAN = 0.01;

function jointOk(j) {
  if (!j) return false;
  const x = Number(j.x);
  const y = Number(j.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  if ((j.score ?? 1) < MIN_SCORE) return false;
  return true;
}

function girdleIndices(joints) {
  if (Array.isArray(joints) && joints.length >= 33) {
    return { lSho: 11, rSho: 12, lHip: 23, rHip: 24 };
  }
  return { lSho: 5, rSho: 6, lHip: 11, rHip: 12 };
}

function yawDeg(a, b) {
  return (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
}

export function normalizeSignedDeg(deg) {
  let d = Number(deg);
  if (!Number.isFinite(d)) return null;
  while (d > 180) d -= 360;
  while (d <= -180) d += 360;
  return d;
}

function distPointToSegment(px, py, ax, ay, bx, by) {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-12) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - ax - t * dx, py - ay - t * dy);
}

function normalizeCorners(raw) {
  if (!Array.isArray(raw) || raw.length !== 4) return null;
  const pts = raw.map((c) => ({ x: Number(c.x), y: Number(c.y) }));
  if (pts.some((p) => !Number.isFinite(p.x) || !Number.isFinite(p.y))) {
    return null;
  }
  return pts;
}

function nearestTableEdge(corners, point) {
  let best = null;
  let bestDist = Infinity;
  for (let i = 0; i < corners.length; i += 1) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    const d = distPointToSegment(point.x, point.y, a.x, a.y, b.x, b.y);
    if (d < bestDist) {
      bestDist = d;
      best = { a, b };
    }
  }
  return best;
}

/**
 * Signed trunk yaw vs the nearer table end line.
 * @param {Array|null} joints
 * @param {Array<{x:number,y:number}>|null} tableCorners
 */
export function trunkRotationDegVsTable(joints, tableCorners) {
  const corners = normalizeCorners(tableCorners);
  if (!corners || !Array.isArray(joints) || joints.length < 13) return null;

  const { lSho, rSho, lHip, rHip } = girdleIndices(joints);
  const ls = joints[lSho];
  const rs = joints[rSho];
  const lh = joints[lHip];
  const rh = joints[rHip];
  if (!jointOk(ls) || !jointOk(rs) || !jointOk(lh) || !jointOk(rh)) return null;

  const leftShoulder = { x: Number(ls.x), y: Number(ls.y) };
  const rightShoulder = { x: Number(rs.x), y: Number(rs.y) };
  const shoulderSpan = Math.hypot(
    rightShoulder.x - leftShoulder.x,
    rightShoulder.y - leftShoulder.y
  );
  if (!(shoulderSpan >= MIN_SPAN)) return null;

  const midHip = {
    x: (Number(lh.x) + Number(rh.x)) / 2,
    y: (Number(lh.y) + Number(rh.y)) / 2,
  };
  const edge = nearestTableEdge(corners, midHip);
  if (!edge) return null;

  const shoulderAngle = yawDeg(leftShoulder, rightShoulder);
  const edgeAngle = yawDeg(edge.a, edge.b);
  let diff = normalizeSignedDeg(shoulderAngle - edgeAngle);
  if (diff == null) return null;
  if (Math.abs(diff) > 90) {
    diff = diff > 0 ? diff - 180 : diff + 180;
  }
  return diff;
}

/**
 * @param {Array|null|undefined} joints COCO-17 or MediaPipe-33
 * @param {{ tableCorners?: Array|null, shoulderWidthRef?: number|null }} [opts]
 */
export function estimateTrunkRotation(joints, opts = {}) {
  const empty = {
    shoulderSpan: null,
    trunkDegVsTable: null,
    shoulderHipSepDeg: null,
    midShoulder: null,
    leftShoulder: null,
    rightShoulder: null,
    leftHip: null,
    rightHip: null,
  };
  if (!Array.isArray(joints) || joints.length < 13) return empty;

  const { lSho, rSho, lHip, rHip } = girdleIndices(joints);
  const ls = joints[lSho];
  const rs = joints[rSho];
  const lh = joints[lHip];
  const rh = joints[rHip];

  const leftShoulder = jointOk(ls) ? { x: Number(ls.x), y: Number(ls.y) } : null;
  const rightShoulder = jointOk(rs) ? { x: Number(rs.x), y: Number(rs.y) } : null;
  const leftHip = jointOk(lh) ? { x: Number(lh.x), y: Number(lh.y) } : null;
  const rightHip = jointOk(rh) ? { x: Number(rh.x), y: Number(rh.y) } : null;

  let shoulderSpan = null;
  let midShoulder = null;
  if (leftShoulder && rightShoulder) {
    shoulderSpan = Math.hypot(
      rightShoulder.x - leftShoulder.x,
      rightShoulder.y - leftShoulder.y
    );
    if (!(shoulderSpan >= MIN_SPAN)) shoulderSpan = null;
    midShoulder = {
      x: (leftShoulder.x + rightShoulder.x) / 2,
      y: (leftShoulder.y + rightShoulder.y) / 2,
    };
  }

  let shoulderHipSepDeg = null;
  if (leftShoulder && rightShoulder && leftHip && rightHip) {
    const sho = yawDeg(leftShoulder, rightShoulder);
    const hip = yawDeg(leftHip, rightHip);
    shoulderHipSepDeg = Math.abs(normalizeSignedDeg(sho - hip) ?? 0);
  }

  const trunkDegVsTable = trunkRotationDegVsTable(joints, opts.tableCorners);

  return {
    shoulderSpan,
    trunkDegVsTable,
    shoulderHipSepDeg,
    midShoulder,
    leftShoulder,
    rightShoulder,
    leftHip,
    rightHip,
  };
}

export function trunkRotationDegForOverlay(estimate) {
  if (!estimate) return null;
  const tableRel = estimate.trunkDegVsTable;
  if (tableRel != null && Number.isFinite(Number(tableRel))) {
    return Number(tableRel);
  }
  return null;
}
